/**
 * Contact Us Page
 *
 * Contact form, office details, and map
 * Task: T4.3.3 - Contact Us page
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineGrid,
  Box,
  TextField,
  Select,
  Button,
  Banner,
  FormLayout,
} from '@shopify/polaris';
import { useSettingsStore } from '../store/settingsStore';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const subjectOptions = [
  { label: 'Select a subject', value: '' },
  { label: 'General Inquiry', value: 'general' },
  { label: 'Product Information', value: 'product' },
  { label: 'Request a Quote', value: 'quote' },
  { label: 'Bulk Order Inquiry', value: 'bulk-order' },
  { label: 'Sample Request', value: 'sample' },
  { label: 'Complaint/Feedback', value: 'feedback' },
  { label: 'Partnership Opportunity', value: 'partnership' },
  { label: 'Other', value: 'other' },
];

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

export function ContactPage() {
  // Get company settings from store
  const { company, fetchSettings } = useSettingsStore();

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<ContactFormData>>({});

  const handleChange = useCallback(
    (field: keyof ContactFormData) => (value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    },
    []
  );

  const validate = useCallback(() => {
    const newErrors: Partial<ContactFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (formData.phone && !/^[0-9+\-\s()]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.subject) {
      newErrors.subject = 'Please select a subject';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/content/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to send message. Please try again.');
      }
    } catch {
      setError('Failed to send message. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [formData, validate]);

  return (
    <Page title="Contact Us">
      <Layout>
        {/* Header */}
        <Layout.Section>
          <Card>
            <Box padding="600" background="bg-surface-success">
              <BlockStack gap="200" align="center">
                <Text as="h1" variant="headingXl" alignment="center">
                  Get in Touch
                </Text>
                <Text as="p" variant="bodyLg" alignment="center" tone="subdued">
                  Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
                </Text>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Main Content */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, md: '2fr 1fr' }} gap="400">
            {/* Contact Form */}
            <Card>
              <Box padding="600">
                <BlockStack gap="400">
                  <Text as="h2" variant="headingLg">
                    Send us a Message
                  </Text>

                  {success && (
                    <Banner tone="success" onDismiss={() => setSuccess(false)}>
                      Thank you for your message! We'll get back to you within 24-48 hours.
                    </Banner>
                  )}

                  {error && (
                    <Banner tone="critical" onDismiss={() => setError(null)}>
                      {error}
                    </Banner>
                  )}

                  <FormLayout>
                    <FormLayout.Group>
                      <TextField
                        label="Full Name"
                        value={formData.name}
                        onChange={handleChange('name')}
                        error={errors.name}
                        autoComplete="name"
                        requiredIndicator
                      />

                      <TextField
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange('email')}
                        error={errors.email}
                        autoComplete="email"
                        requiredIndicator
                      />
                    </FormLayout.Group>

                    <FormLayout.Group>
                      <TextField
                        label="Phone Number"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange('phone')}
                        error={errors.phone}
                        autoComplete="tel"
                        helpText="Optional"
                      />

                      <Select
                        label="Subject"
                        options={subjectOptions}
                        value={formData.subject}
                        onChange={handleChange('subject')}
                        error={errors.subject}
                        requiredIndicator
                      />
                    </FormLayout.Group>

                    <TextField
                      label="Message"
                      value={formData.message}
                      onChange={handleChange('message')}
                      error={errors.message}
                      multiline={4}
                      autoComplete="off"
                      requiredIndicator
                      helpText="Please provide as much detail as possible"
                    />

                    <Button
                      variant="primary"
                      onClick={handleSubmit}
                      loading={loading}
                      fullWidth
                    >
                      Send Message
                    </Button>
                  </FormLayout>
                </BlockStack>
              </Box>
            </Card>

            {/* Contact Info */}
            <BlockStack gap="400">
              <Card>
                <Box padding="500">
                  <BlockStack gap="300">
                    <Text as="h2" variant="headingMd">
                      Contact Information
                    </Text>

                    <BlockStack gap="400">
                      <BlockStack gap="100">
                        <Text as="p" variant="bodySm" fontWeight="semibold">
                          Email
                        </Text>
                        <Text as="p" variant="bodyMd">
                          {company.email}
                        </Text>
                      </BlockStack>

                      <BlockStack gap="100">
                        <Text as="p" variant="bodySm" fontWeight="semibold">
                          Phone
                        </Text>
                        <Text as="p" variant="bodyMd">
                          {company.phone}
                        </Text>
                      </BlockStack>

                      <BlockStack gap="100">
                        <Text as="p" variant="bodySm" fontWeight="semibold">
                          Address
                        </Text>
                        <Text as="p" variant="bodyMd">
                          {company.name}
                          <br />
                          {company.address}
                        </Text>
                      </BlockStack>
                    </BlockStack>
                  </BlockStack>
                </Box>
              </Card>

              <Card>
                <Box padding="500">
                  <BlockStack gap="300">
                    <Text as="h2" variant="headingMd">
                      Business Hours
                    </Text>

                    <BlockStack gap="200">
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text as="span" variant="bodyMd">Monday - Friday</Text>
                        <Text as="span" variant="bodyMd">9:00 AM - 6:00 PM</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text as="span" variant="bodyMd">Saturday</Text>
                        <Text as="span" variant="bodyMd">10:00 AM - 4:00 PM</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text as="span" variant="bodyMd">Sunday</Text>
                        <Text as="span" variant="bodyMd" tone="subdued">Closed</Text>
                      </div>
                    </BlockStack>
                  </BlockStack>
                </Box>
              </Card>

              {/* Map Placeholder */}
              <Card>
                <Box
                  padding="400"
                  minHeight="200px"
                  background="bg-surface-secondary"
                >
                  <BlockStack align="center" gap="200">
                    <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                      📍 Map
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                      Interactive map will be displayed here
                    </Text>
                  </BlockStack>
                </Box>
              </Card>
            </BlockStack>
          </InlineGrid>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
